import styled from "styled-components";
import tw from "tailwind-styled-components";
import React, { useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import Highlighter from "react-highlight-words";
import PropTypes from "prop-types";

import { getHumanUrl, replaceHumanTagsWithSlugs, timeAgo } from "../utils";
import { extractPositiveNegativeQuery } from "../selectors/humans";
import Avatar from "./Avatar";

export const HumanListRowStyled = tw.li`
cursor-pointer
focus:bg-gray-200
focus:outline-none
`;

export const HumanNameAndHighlight = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 10px;
  overflow: hidden;
  color: #343434;
`;

export const HumanName = styled.div`
  font-weight: 600;
`;

export const SearchHighlight = styled.div`
  font-size: 12px;
  > span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
  }
  .highlighted-text {
    font-weight: bold;
    padding: 0;
  }
`;

export const NO_NAME_PLACEHOLDER = "No Name";
// Offset used to determine if the text should be cut and
// move backward to be able to view on the area.
const OFFSET_FROM_START = 10;

const showHighlitedText = (text, positiveQuery) => {
  const textLower = text.toLowerCase();

  const indexOfFirst = Math.min(
    ...positiveQuery.map((q) => textLower.indexOf(q))
  );

  if (indexOfFirst <= OFFSET_FROM_START) return text;

  // show the line where the text match
  return `...${text.substr(indexOfFirst - OFFSET_FROM_START)}`;
};

const HumanListRow = React.memo(({ human, isSelected, query, comments }) => {
  const routerHistory = useHistory();

  const [positiveQuery] = extractPositiveNegativeQuery(query);
  let searchMatch = "";
  // find first matching comment,
  // assuming comments never have negative keywords (negativeQuery)
  // since humans are already filtered
  for (let comment of comments) {
    const cleanText = replaceHumanTagsWithSlugs(comment.text);
    const cleanTextLower = cleanText.toLowerCase();

    const hasMatch = positiveQuery.every((q) => cleanTextLower.includes(q));
    if (hasMatch) {
      searchMatch = cleanText;
      break;
    }
  }

  const onEnterKeyPress = useCallback(
    (e) => {
      if (e.key !== "Enter") return;

      e.preventDefault();
      e.stopPropagation(); // adds new line to input if not stopped

      routerHistory.push({
        pathname: getHumanUrl(human),
      });
    },
    [routerHistory, human]
  );

  return (
    <HumanListRowStyled
      className="human-list-row"
      onKeyDown={onEnterKeyPress}
      tabIndex="0"
    >
      <Link
        className={`block px-2 py-2 ${isSelected ? "bg-gray-300" : ""}`}
        to={getHumanUrl(human)}
      >
        <div className="flex space-x-3 overflow-hidden">
          <Avatar human={human} isEditable={false} size="45px" />
          <div className="flex-1 space-y-1 min-w-0">
            {/* min-w-0 fixes Highlighter overflowing */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">
                {human.name ? human.name : NO_NAME_PLACEHOLDER}
              </h3>
              <div className="text-xs font-light text-gray-500">
                {timeAgo(human.computedUpdatedDate)}
              </div>
            </div>
            {positiveQuery.length > 0 && (
              <SearchHighlight data-testid="search-highlighter">
                <Highlighter
                  highlightClassName="highlighted-text"
                  searchWords={positiveQuery}
                  autoEscape={true}
                  textToHighlight={showHighlitedText(
                    searchMatch,
                    positiveQuery
                  )}
                />
              </SearchHighlight>
            )}
          </div>
        </div>
      </Link>
    </HumanListRowStyled>
  );
});

HumanListRow.propTypes = {
  human: PropTypes.object,
  isSelected: PropTypes.bool,
  query: PropTypes.any,
  comments: PropTypes.any,
};

HumanListRow.displayName = "HumanListRow";

export default HumanListRow;
